import CredentialButtons from '@/components/credentials/CredentialButtons.jsx';
import Panel from '@/components/panel/Panel.jsx';
import { FiKey, GoLinkExternal } from '@/components/react-icons/main';
import Skeleton from '@/components/Skeleton.jsx';
import useBoundForm from '@/hooks/useBoundForm.js';
import { action } from '@/lib/api.js';
import sendToast from '@/lib/sendToast.js';
import credentialActions from '@/redux/actions/credentialActions.js';
import { getCredentialById, testWorkflowStepCredential } from '@/redux/actions/gravityActions.js';
import { openDocDrawer } from '@/redux/actions/uiActions/drawerActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { query } from '@/redux/selectors/querySelectors.js';
import selectors from '@/redux/selectors/selectors.js';
import argumentDataSources from '@/schema/argumentDataSources.js';
import CredentialStatusTag from '@/screens/credentials/components/CredentialStatusTag.jsx';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { generateDeepDiffs } from '@shared/utils/generateDeepDiffs.js';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ArgumentsCredentialsWrapper from '../ArgumentsCredentialsWrapper.jsx';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';

const getInitialState = (secrets) => ({ ...(secrets || {}) });

const CredentialSlider = ({ onClose, credentialId }) => {
  const credentialsRef = useRef();
  const [initialSecrets, setInitialSecrets] = useState(null);
  const [loading, setLoading] = useState(false);
  const companyId = useSelector(selectors.companyId);
  const credentials = useSelector(selectors.credentials);
  const credential = useArgSelector(selectors.credentialById, credentialId);

  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const stepId = useArgSelector(query, 'stepId');
  const app = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state));
  const appId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.id);
  const appName = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.name);
  const appAuth = useArgSelector(workflowScreenSelectors.activeWorkflowStepActiveAuth, credentialId);
  const canEditCredentials = useArgSelector(selectors.permission, 'credential:edit');

  const form = useBoundForm({ initialState: getInitialState(credential?.secrets) });

  const handleClose = async (shouldConfirm = true) => {
    const result = await onClose?.(shouldConfirm);

    if (result) {
      form.reset();
      action('SET_ARGUMENT_OPTION_HAS_BEEN_MODIFIED', false);
    }
  };

  const handleChange = ({ key, value }) => {
    form.set(key, value);
  };

  const allCredentials = credentials
    ?.filter((n) => n.appId === appId && n.companyId !== 'MINDCLOUD' && n.companyId === companyId) //
    .sort(sortByProp(['valid', 'updatedOn'], ['desc', 'desc']));

  const handleTest = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const data = await testWorkflowStepCredential({ workflowId, stepId });
    await getCredentialById(credentialId, { secrets: true, companyId });

    if (data?.success) {
      sendToast({ message: 'Connection is working and ready to use!', type: 'success', duration: 4000 });
    } else {
      sendToast({ message: 'Invalid connection, please review the provided values!', type: 'error', duration: 4000 });
    }
  };

  const handleDelete = async () => {
    const isUsedInProduction = Number(credential?.activeWorkflowCount) > 0;
    if (isUsedInProduction) {
      setModalState('cannotDeleteCredential', { open: true, credentialId });
      return;
    }
    credentialActions.deleteCredential({ credentialId, companyId, appId });
    const nextBestCredential = allCredentials?.find((n) => n.credentialId !== credentialId && n?.status === 'valid');
    stepActions.set('credentialId', nextBestCredential?.credentialId ?? null);
    handleClose();
  };

  const handleHelp = () => {
    if (appAuth?.helpDocumentUrl) {
      window.open(appAuth?.helpDocumentUrl, '_blank');
      return;
    }
    openDocDrawer({ docId: appAuth?.helpDocumentId });
  };

  useEffect(() => {
    const diffs = generateDeepDiffs(form.state, getInitialState(credential?.secrets));
    action('SET_ARGUMENT_OPTION_HAS_BEEN_MODIFIED', diffs?.length > 0);
  }, [JSON.stringify(form.state), JSON.stringify(credential?.secrets)]);

  useEffect(() => {
    form.setState(getInitialState(credential?.secrets));
  }, [JSON.stringify(credential?.secrets)]);

  useEffect(() => {
    const loadCredential = async () => {
      if (credentialId) {
        setLoading(true);
        await getCredentialById(credentialId, { secrets: true, companyId }, () => {
          setLoading(false);
        });
      }
    };

    loadCredential();
  }, [credentialId]);

  useEffect(() => {
    const cleanSecrets = { ...credential?.secrets, label: undefined, custom: undefined };
    const credentialExists = allCredentials?.find((n) => n?.credentialId === credentialId);
    if (!credentialExists) {
      return;
    }

    if (cleanSecrets && JSON.stringify(cleanSecrets) !== JSON.stringify(initialSecrets)) {
      setInitialSecrets(cleanSecrets);
    }
  }, [JSON.stringify(credential?.secrets)]);

  const title = !credential?.status || credential?.status === 'incomplete' ? `Connect to ${appName}` : `Edit ${appName} Connection`;

  return (
    <>
      <SliderHeader onClose={handleClose} showDelete={true} onDelete={handleDelete} />
      <SliderTitle //
        icon={FiKey}
        title={title}
        description={'Connections are how we log in to your third-party applications.'}
        className={'margin-bottom'}
        tag={<CredentialStatusTag credential={credential} />}
      />
      {(appAuth?.helpDocumentId || appAuth?.helpDocumentUrl) && (
        <Panel.ActionButton onClick={handleHelp} icon={GoLinkExternal}>
          Instructions for Setting Up Connection
        </Panel.ActionButton>
      )}
      {loading && <Skeleton count={3} variant={'light'} skeletonStyle={{ height: '20px' }} />}
      {!loading && (
        <ArgumentsCredentialsWrapper //
          ref={credentialsRef}
          key={!!credential?.secrets}
          formSecrets={form.state}
          initialSecrets={initialSecrets}
          dataSource={argumentDataSources.workflowStep}
          credentialId={credentialId}
          disabled={!canEditCredentials}
          onChange={handleChange}
          credentialSlider
        />
      )}
      <CredentialButtons //
        app={app}
        credential={credential}
        credentialsRef={credentialsRef}
        loading={loading}
        previousValue={credential?.secrets}
        currentValue={form.state}
        onTest={handleTest}
      />
      <SliderClose onClose={handleClose} showMessage={false} marginTopAuto />
    </>
  );
};

export default CredentialSlider;
